import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Styles
import './group-selection.scss';

// Components
import NewGroupItem from './item/new-group-item';
import GroupSelectionItem from './item/group-selection-item';

// Context
import SchoolCourseContext from './context/school-course-context';
import CourseInformationContext from './context/course-information-context';

// Helpers
import { DEFAULT_GROUP_MODAL_ID, MAX_GROUP_LENGTH, NEW_GROUP_MODAL_ID } from '../../helpers/enums';

function GroupSelection(props) {
    const { i18n } = useLocalisation();

    const {
        forwardRef,
        currentClassId,
        productId,
        classGroups,
        importedClasses,
        resetEditInformationHandler,
        isFetchingEditInformation,
        showNewCourses,
        newCoursesAvailabilityCriteria,
        // Handlers
        editGroupHandler,
        deleteGroupHandler,
        newGroupHandler,
        getDefaultCourseInformation,
        getEditGroupCourseInformation,
        getCourseYears,
        getDefaultCourse,
        getCourses,
        resetYearOptions,
        updateGroupCheckbox,
        setShownPassword
    } = props;

    // State variables to handle the visibility of a modal relative to the group that is selected
    const [groupModalId, setGroupModalId] = useState(DEFAULT_GROUP_MODAL_ID);
    const [groupModalVisibility, setGroupModalVisibility] = useState(false);
    const [isSchoolCoursesEnabled, setIsSchoolCoursesEnabled] = useState(false);
    const [groupCountryCode, setGroupCountryCode] = useState('');
    const [isEditingMode, setIsEditingMode] = useState(false);
    const [isNewGroupModal, setIsNewGroupModal] = useState(false);

    // Context variables
    const { countrySelectValue, yearSelectValue } = useContext(CourseInformationContext);

    // Handler
    const showModal = ({ modalId, isClosing, setEditMode }) => {
        if (isSchoolCoursesEnabled === true) {
            setIsSchoolCoursesEnabled(false);
        }
        setGroupModalId(modalId);

        if (isClosing) {
            setGroupModalVisibility(false);
        } else {
            setGroupModalVisibility(prevState => !prevState);
        }

        if (setEditMode && isEditingMode === false && modalId === -1) {
            // to set the flag for new group button clicked
            setIsNewGroupModal(true);
            setIsEditingMode(true);
        } else if (setEditMode && isEditingMode === false) {
            setIsEditingMode(true);
        } else {
            setIsEditingMode(false);
        }
    };
    const localNewGroupHandler = async ({ name }) => newGroupHandler({ name });

    // Effects
    // - Retrieve the list of countries
    useEffect(() => {
        const getEditGroupCourseInfo = async () => {
            // Get default course data when edit group button is clicked
            const information = await getEditGroupCourseInformation({
                classId: currentClassId,
                productId,
                groupId: groupModalId
            });

            await setGroupCountryCode(information.groupCountryCode);

            if (information && information.isSchoolCoursesEnabled) {
                await setIsSchoolCoursesEnabled(information.isSchoolCoursesEnabled);
            }
        };

        const getDefaulCourseInfo = async () => {
            // Get default data when new group button is clicked
            const countryResponse = await getDefaultCourse({
                productId,
                classId: currentClassId
            });
            if (countryResponse && countryResponse.valid) {
                const defaultData = {
                    data: countryResponse.data
                };
                const information = await getDefaultCourseInformation({
                    defaultData
                });

                await setGroupCountryCode(information.groupCountryCode);

                if (information && information.isSchoolCoursesEnabled) {
                    await setIsSchoolCoursesEnabled(information.isSchoolCoursesEnabled);
                }
            } else {
                // error handling
            }
        };

        if (isEditingMode === true && groupModalVisibility === true && isNewGroupModal === true) {
            // Get the auto-populated fields for the country + grade + course for new group
            getDefaulCourseInfo();
            setIsNewGroupModal(false);
        } else if (isEditingMode === true && groupModalVisibility === true) {
            // code to get data for EDIT group
            getEditGroupCourseInfo();
        }
    }, [groupModalVisibility, productId]);

    // Reset the selection for years and courses when school course is enabled
    useEffect(() => {
        if (isSchoolCoursesEnabled === false) {
            resetYearOptions();
        }
    }, [isSchoolCoursesEnabled]);

    // - Retrieve the list of years
    useEffect(() => {
        const countryValidValue = countrySelectValue !== '' && isFetchingEditInformation === false;
        const isNewGroupCourseEnabled = isSchoolCoursesEnabled === true && isFetchingEditInformation === false;

        // Either there's a valid country value or school course was selected, then retrieve the list of countries
        if (countryValidValue || isNewGroupCourseEnabled) {
            const countryCode = isSchoolCoursesEnabled ? undefined : countrySelectValue;
            getCourseYears({
                countryCode
            });
        }
    }, [countrySelectValue, isSchoolCoursesEnabled, productId]);

    // - Retrieve the list of courses
    useEffect(() => {
        if (yearSelectValue !== '' && isFetchingEditInformation === false) {
            const countryCode = isSchoolCoursesEnabled ? undefined : countrySelectValue;
            getCourses({
                countryCode,
                yearGroup: yearSelectValue,
                productId,
                noCourseText: i18n._(t`Group Modal - label - Select no courses`)
            });
        }
    }, [yearSelectValue, productId]);

    const resetEditFetchInformation = (value) => {
        resetEditInformationHandler();
        setIsSchoolCoursesEnabled(value);
    };

    return (
        <div ref={forwardRef} className={`groups__filters ${importedClasses}`}>
            <SchoolCourseContext.Provider value={{
                isSchoolCoursesEnabled,
                setIsSchoolCoursesEnabled: resetEditFetchInformation,
                groupCountryCode
            }}>
                {
                    classGroups && classGroups.map((classGroupItem, index) => (
                        <GroupSelectionItem
                            key={classGroupItem.name}
                            classGroupItem={classGroupItem}
                            showModal={showModal}
                            getDefaultCourseInformation={getDefaultCourseInformation}
                            getEditGroupCourseInformation={getEditGroupCourseInformation}
                            index={index}
                            groupModalVisibility={groupModalVisibility}
                            groupModalId={groupModalId}
                            productId={productId}
                            editGroupHandler={editGroupHandler}
                            deleteGroupHandler={deleteGroupHandler}
                            updateGroupCheckbox={updateGroupCheckbox}
                            setShownPassword={setShownPassword}
                            showNewCourses={showNewCourses}
                            newCoursesAvailabilityCriteria={newCoursesAvailabilityCriteria}
                        />
                    ))
                }
                {
                    currentClassId !== 0 ? (
                        <NewGroupItem
                            showModal={showModal}
                            currentGroupId={NEW_GROUP_MODAL_ID}
                            isBtnDisabled={classGroups.length === MAX_GROUP_LENGTH + 1} // cater for the show all btn
                            groupModalVisibility={groupModalVisibility}
                            groupModalId={groupModalId}
                            productId={productId}
                            newGroupHandler={localNewGroupHandler}
                            showNewCourses={showNewCourses}
                            newCoursesAvailabilityCriteria={newCoursesAvailabilityCriteria}
                        />
                    ) : ''
                }
            </SchoolCourseContext.Provider>
        </div>
    );
}

GroupSelection.propTypes = {
    forwardRef: PropTypes.any,
    currentClassId: PropTypes.number,
    productId: PropTypes.number,
    classGroups: PropTypes.array,
    importedClasses: PropTypes.string,
    isFetchingEditInformation: PropTypes.bool,
    showNewCourses: PropTypes.bool,
    newCoursesAvailabilityCriteria: PropTypes.object,
    // Handlers
    getDefaultCourseInformation: PropTypes.func,
    getEditGroupCourseInformation: PropTypes.func.isRequired,
    getDefaultCourse: PropTypes.func.isRequired,
    getCourseYears: PropTypes.func.isRequired,
    getCourses: PropTypes.func.isRequired,
    newGroupHandler: PropTypes.func.isRequired,
    editGroupHandler: PropTypes.func.isRequired,
    deleteGroupHandler: PropTypes.func.isRequired,
    resetYearOptions: PropTypes.func.isRequired,
    resetEditInformationHandler: PropTypes.func,
    updateGroupCheckbox: PropTypes.func.isRequired,
    setShownPassword: PropTypes.func
};

export default React.forwardRef((props, ref) => (<GroupSelection {...props} forwardRef={ref} />));



// WEBPACK FOOTER //
// ./components/groups/group-selection.js